import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Shield,
  Package,
  Info,
  DollarSign,
  ClipboardList,
  Check,
  Camera,
  X
} from 'lucide-react';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../api/auth.api';
import {
  SpecialRequirementsData,
  SpecialRequirement,
  ChangeOrder,
  PunchListItem
} from '../../types/backend.types';

// Inline styles for this component
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '16px',
    maxWidth: '600px',
    margin: '0 auto',
    paddingBottom: '100px'
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1e3a5f',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b'
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: '16px',
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    cursor: 'pointer'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e3a5f'
  },
  sectionContent: {
    padding: '16px'
  },
  tmReminder: {
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px'
  },
  tmTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '18px',
    fontWeight: 700,
    color: '#92400e',
    marginBottom: '12px'
  },
  tmItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 0',
    borderBottom: '1px dashed #fbbf24'
  },
  tmItemLast: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 0'
  },
  tmItemText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#78350f'
  },
  requirementItem: {
    padding: '12px',
    marginBottom: '8px',
    borderRadius: '8px',
    backgroundColor: '#f8fafc'
  },
  requirementSafety: {
    borderLeft: '4px solid #ef4444',
    backgroundColor: '#fef2f2'
  },
  requirementMaterials: {
    borderLeft: '4px solid #3b82f6',
    backgroundColor: '#eff6ff'
  },
  requirementGeneral: {
    borderLeft: '4px solid #6b7280',
    backgroundColor: '#f9fafb'
  },
  changeOrderItem: {
    padding: '12px',
    marginBottom: '8px',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0'
  },
  coHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  coNumber: {
    fontWeight: 600,
    color: '#1e3a5f'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500
  },
  statusPending: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  statusApproved: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  statusRejected: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  coDescription: {
    fontSize: '14px',
    color: '#475569',
    marginTop: '4px'
  },
  coAmount: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1e3a5f',
    marginTop: '4px'
  },
  punchItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    marginBottom: '8px',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0'
  },
  checkbox: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    border: '2px solid #cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: '2px'
  },
  checkboxChecked: {
    backgroundColor: '#22c55e',
    borderColor: '#22c55e'
  },
  punchContent: {
    flex: 1
  },
  punchDescription: {
    fontSize: '15px',
    color: '#1e3a5f',
    marginBottom: '4px'
  },
  punchMeta: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px',
    color: '#64748b'
  },
  priorityBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'uppercase' as const
  },
  priorityUrgent: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  priorityHigh: {
    backgroundColor: '#ffedd5',
    color: '#9a3412'
  },
  priorityNormal: {
    backgroundColor: '#e0e7ff',
    color: '#3730a3'
  },
  priorityLow: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563'
  },
  acknowledgeButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#1e3a5f',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '24px'
  },
  acknowledgeButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    gap: '16px'
  },
  errorContainer: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#ef4444'
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '20px',
    color: '#64748b',
    fontSize: '14px'
  }
};

// Translations for this component
const translations = {
  en: {
    title: 'Job Requirements',
    subtitle: 'Please review before starting work',
    tmJobTitle: 'T&M Job Reminders',
    tmReminder1: 'Must create a ticket EVERY DAY',
    tmReminder2: 'Track all quantities carefully',
    tmReminder3: 'SAVE ALL TICKETS',
    requirements: 'Special Requirements',
    changeOrders: 'Change Orders',
    punchList: 'Punch List',
    acknowledge: 'I Acknowledge & Understand',
    acknowledging: 'Acknowledging...',
    loading: 'Loading requirements...',
    error: 'Failed to load requirements',
    noRequirements: 'No special requirements for this job',
    noChangeOrders: 'No change orders for this job',
    noPunchList: 'No open punch list items',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    dueDate: 'Due',
    safety: 'Safety',
    materials: 'Materials',
    general: 'General',
    markComplete: 'Mark as complete',
    completed: 'Completed',
    completeItem: 'Complete Item',
    completeWithPhoto: 'Complete with Photo',
    completeWithoutPhoto: 'Complete without Photo',
    cancel: 'Cancel',
    takingPhoto: 'Taking photo...',
    uploading: 'Uploading...'
  },
  es: {
    title: 'Requisitos del Trabajo',
    subtitle: 'Por favor revise antes de comenzar a trabajar',
    tmJobTitle: 'Recordatorios de Trabajo T&M',
    tmReminder1: 'Debe crear un ticket TODOS LOS DÍAS',
    tmReminder2: 'Seguir todas las cantidades cuidadosamente',
    tmReminder3: 'GUARDAR TODOS LOS TICKETS',
    requirements: 'Requisitos Especiales',
    changeOrders: 'Órdenes de Cambio',
    punchList: 'Lista de Pendientes',
    acknowledge: 'Reconozco y Entiendo',
    acknowledging: 'Reconociendo...',
    loading: 'Cargando requisitos...',
    error: 'Error al cargar requisitos',
    noRequirements: 'Sin requisitos especiales para este trabajo',
    noChangeOrders: 'Sin órdenes de cambio para este trabajo',
    noPunchList: 'Sin elementos pendientes',
    pending: 'Pendiente',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    dueDate: 'Vence',
    safety: 'Seguridad',
    materials: 'Materiales',
    general: 'General',
    markComplete: 'Marcar como completado',
    completed: 'Completado',
    completeItem: 'Completar Item',
    completeWithPhoto: 'Completar con Foto',
    completeWithoutPhoto: 'Completar sin Foto',
    cancel: 'Cancelar',
    takingPhoto: 'Tomando foto...',
    uploading: 'Subiendo...'
  }
};

const SpecialRequirements: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];

  // Get job data from navigation state
  const jobId = location.state?.jobId;
  const returnPath = location.state?.returnPath || '/';

  const [data, setData] = useState<SpecialRequirementsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [completingItems, setCompletingItems] = useState<Set<number>>(new Set());

  // Completion confirmation modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedPunchItem, setSelectedPunchItem] = useState<PunchListItem | null>(null);
  const [completionPhoto, setCompletionPhoto] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Section expand/collapse state
  const [expandedSections, setExpandedSections] = useState({
    requirements: true,
    changeOrders: true,
    punchList: true
  });

  // Load special requirements data
  useEffect(() => {
    const loadData = async () => {
      if (!jobId) {
        setError('No job specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/api/special-requirements/job/${jobId}`);
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError(response.data.error || t.error);
        }
      } catch (err: any) {
        console.error('Error loading special requirements:', err);
        setError(err.message || t.error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [jobId, t.error]);

  // Prevent back navigation until acknowledged
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      // Push state back to prevent navigation
      window.history.pushState(null, '', window.location.pathname);
    };

    // Push initial state
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAcknowledge = async () => {
    if (!jobId) return;

    try {
      setAcknowledging(true);
      await api.post(`/api/special-requirements/acknowledge/${jobId}`);
      // Navigate back to return path
      navigate(returnPath, { replace: true });
    } catch (err: any) {
      console.error('Error acknowledging:', err);
      setError(err.message || 'Failed to acknowledge');
    } finally {
      setAcknowledging(false);
    }
  };

  // Open completion modal for a punch item
  const openCompleteModal = (item: PunchListItem) => {
    setSelectedPunchItem(item);
    setCompletionPhoto(null);
    setShowCompleteModal(true);
  };

  // Take a photo for completion
  const takeCompletionPhoto = async () => {
    try {
      setUploadingPhoto(true);
      const photo = await CapacitorCamera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera
      });

      if (photo.base64String) {
        // Upload to Azure Blob Storage
        const response = await api.post('/api/photos/upload', {
          base64: photo.base64String,
          container: 'daily-reports',
          filename: `punch-${selectedPunchItem?.punch_id}-${Date.now()}.jpg`
        });

        if (response.data.success) {
          setCompletionPhoto(response.data.data.url);
        }
      }
    } catch (err: any) {
      console.error('Error taking/uploading photo:', err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Complete the punch item (with or without photo)
  const handleCompletePunchItem = async (withPhoto: boolean = false) => {
    if (!selectedPunchItem || completingItems.has(selectedPunchItem.punch_id)) return;

    const punchId = selectedPunchItem.punch_id;

    try {
      setCompletingItems(prev => new Set(prev).add(punchId));

      // If photo requested but not taken yet, take it first
      if (withPhoto && !completionPhoto) {
        await takeCompletionPhoto();
        // User will click complete again after photo is taken
        setCompletingItems(prev => {
          const next = new Set(prev);
          next.delete(punchId);
          return next;
        });
        return;
      }

      await api.put(`/api/special-requirements/punch-list/${punchId}/complete`, {
        photo_url: completionPhoto || null
      });

      // Update local state to mark item as completed
      if (data) {
        setData({
          ...data,
          punchList: data.punchList.map(item =>
            item.punch_id === punchId
              ? { ...item, status: 'completed' }
              : item
          )
        });
      }

      // Close modal
      setShowCompleteModal(false);
      setSelectedPunchItem(null);
      setCompletionPhoto(null);
    } catch (err: any) {
      console.error('Error completing punch item:', err);
    } finally {
      setCompletingItems(prev => {
        const next = new Set(prev);
        next.delete(punchId);
        return next;
      });
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'approved':
        return { ...styles.statusBadge, ...styles.statusApproved };
      case 'rejected':
        return { ...styles.statusBadge, ...styles.statusRejected };
      default:
        return { ...styles.statusBadge, ...styles.statusPending };
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return t.approved;
      case 'rejected':
        return t.rejected;
      default:
        return t.pending;
    }
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return { ...styles.priorityBadge, ...styles.priorityUrgent };
      case 'high':
        return { ...styles.priorityBadge, ...styles.priorityHigh };
      case 'low':
        return { ...styles.priorityBadge, ...styles.priorityLow };
      default:
        return { ...styles.priorityBadge, ...styles.priorityNormal };
    }
  };

  const getRequirementStyle = (category: string) => {
    switch (category) {
      case 'safety':
        return { ...styles.requirementItem, ...styles.requirementSafety };
      case 'materials':
        return { ...styles.requirementItem, ...styles.requirementMaterials };
      default:
        return { ...styles.requirementItem, ...styles.requirementGeneral };
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'safety':
        return <Shield size={16} color="#ef4444" />;
      case 'materials':
        return <Package size={16} color="#3b82f6" />;
      default:
        return <Info size={16} color="#6b7280" />;
    }
  };

  const getCategoryText = (category: string) => {
    switch (category) {
      case 'safety':
        return t.safety;
      case 'materials':
        return t.materials;
      default:
        return t.general;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  // Get text in current language, falling back to English
  const getText = (en: string, es?: string) => {
    if (language === 'es' && es) return es;
    return en;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader2 size={40} className="animate-spin" color="#1e3a5f" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertCircle size={40} />
          <p>{error || t.error}</p>
        </div>
      </div>
    );
  }

  const hasRequirements = data.requirements.length > 0;
  const hasChangeOrders = data.changeOrders.length > 0;
  const hasPunchList = data.punchList.filter(p => p.status !== 'completed').length > 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <AlertTriangle size={28} color="#f59e0b" />
          {t.title}
        </h1>
        <p style={styles.subtitle}>
          {data.job.job_name} - {data.job.bradanna_job_number}
        </p>
        <p style={styles.subtitle}>{t.subtitle}</p>
      </div>

      {/* T&M Job Reminders */}
      {data.isTmJob && (
        <div style={styles.tmReminder}>
          <div style={styles.tmTitle}>
            <DollarSign size={24} />
            {t.tmJobTitle}
          </div>
          <div style={styles.tmItem}>
            <AlertTriangle size={18} color="#92400e" />
            <span style={styles.tmItemText}>{t.tmReminder1}</span>
          </div>
          <div style={styles.tmItem}>
            <AlertTriangle size={18} color="#92400e" />
            <span style={styles.tmItemText}>{t.tmReminder2}</span>
          </div>
          <div style={styles.tmItemLast}>
            <AlertTriangle size={18} color="#92400e" />
            <span style={styles.tmItemText}>{t.tmReminder3}</span>
          </div>
        </div>
      )}

      {/* Special Requirements Section */}
      <div style={styles.section}>
        <div
          style={styles.sectionHeader}
          onClick={() => toggleSection('requirements')}
        >
          <div style={styles.sectionTitle}>
            <FileText size={20} color="#1e3a5f" />
            {t.requirements}
            {hasRequirements && (
              <span style={{
                backgroundColor: '#e0e7ff',
                color: '#3730a3',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                {data.requirements.length}
              </span>
            )}
          </div>
          {expandedSections.requirements ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {expandedSections.requirements && (
          <div style={styles.sectionContent}>
            {hasRequirements ? (
              data.requirements.map((req: SpecialRequirement) => (
                <div key={req.requirement_id} style={getRequirementStyle(req.category)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    {getCategoryIcon(req.category)}
                    <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                      {getCategoryText(req.category)}
                    </span>
                  </div>
                  <p style={{ fontSize: '15px', color: '#1e293b', margin: 0 }}>
                    {getText(req.requirement_text_en, req.requirement_text_es)}
                  </p>
                </div>
              ))
            ) : (
              <p style={styles.emptyState}>{t.noRequirements}</p>
            )}
          </div>
        )}
      </div>

      {/* Change Orders Section */}
      <div style={styles.section}>
        <div
          style={styles.sectionHeader}
          onClick={() => toggleSection('changeOrders')}
        >
          <div style={styles.sectionTitle}>
            <DollarSign size={20} color="#1e3a5f" />
            {t.changeOrders}
            {hasChangeOrders && (
              <span style={{
                backgroundColor: '#e0e7ff',
                color: '#3730a3',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                {data.changeOrders.length}
              </span>
            )}
          </div>
          {expandedSections.changeOrders ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {expandedSections.changeOrders && (
          <div style={styles.sectionContent}>
            {hasChangeOrders ? (
              data.changeOrders.map((co: ChangeOrder) => (
                <div key={co.change_order_id} style={styles.changeOrderItem}>
                  <div style={styles.coHeader}>
                    <span style={styles.coNumber}>CO #{co.co_number}</span>
                    <span style={getStatusBadgeStyle(co.status)}>
                      {getStatusText(co.status)}
                    </span>
                  </div>
                  <p style={styles.coDescription}>{co.description}</p>
                </div>
              ))
            ) : (
              <p style={styles.emptyState}>{t.noChangeOrders}</p>
            )}
          </div>
        )}
      </div>

      {/* Punch List Section */}
      <div style={styles.section}>
        <div
          style={styles.sectionHeader}
          onClick={() => toggleSection('punchList')}
        >
          <div style={styles.sectionTitle}>
            <ClipboardList size={20} color="#1e3a5f" />
            {t.punchList}
            {hasPunchList && (
              <span style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                {data.punchList.filter(p => p.status !== 'completed').length}
              </span>
            )}
          </div>
          {expandedSections.punchList ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {expandedSections.punchList && (
          <div style={styles.sectionContent}>
            {data.punchList.length > 0 ? (
              data.punchList.map((item: PunchListItem) => (
                  <div
                    key={item.punch_id}
                    style={{
                      ...styles.punchItem,
                      opacity: item.status === 'completed' ? 0.6 : 1,
                      backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#f8fafc'
                    }}
                  >
                    <div
                      style={{
                        ...styles.checkbox,
                        ...(item.status === 'completed' ? styles.checkboxChecked : {}),
                        cursor: item.status === 'completed' ? 'default' : 'pointer'
                      }}
                      onClick={() => item.status !== 'completed' && openCompleteModal(item)}
                    >
                      {completingItems.has(item.punch_id) ? (
                        <Loader2 size={14} className="animate-spin" color="white" />
                      ) : item.status === 'completed' ? (
                        <Check size={14} color="white" />
                      ) : null}
                    </div>
                    <div style={styles.punchContent}>
                      <p style={{
                        ...styles.punchDescription,
                        textDecoration: item.status === 'completed' ? 'line-through' : 'none',
                        color: item.status === 'completed' ? '#64748b' : '#1e3a5f'
                      }}>
                        {getText(item.description_en, item.description_es)}
                      </p>
                      <div style={styles.punchMeta}>
                        <span style={getPriorityStyle(item.priority)}>
                          {item.status === 'completed' ? t.completed : item.priority.toUpperCase()}
                        </span>
                        {item.due_date && item.status !== 'completed' && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={12} />
                            {t.dueDate}: {formatDate(item.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <p style={styles.emptyState}>{t.noPunchList}</p>
            )}
          </div>
        )}
      </div>

      {/* Complete Item Confirmation Modal */}
      {showCompleteModal && selectedPunchItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '350px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                {t.completeItem}
              </h3>
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setSelectedPunchItem(null);
                  setCompletionPhoto(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} color="#64748b" />
              </button>
            </div>

            <p style={{ fontSize: '15px', color: '#475569', marginBottom: '20px', lineHeight: 1.5 }}>
              {getText(selectedPunchItem.description_en, selectedPunchItem.description_es)}
            </p>

            {/* Photo Preview */}
            {completionPhoto && (
              <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
                <img
                  src={completionPhoto}
                  alt="Completion"
                  style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Take Photo Button */}
              {!completionPhoto && (
                <button
                  onClick={takeCompletionPhoto}
                  disabled={uploadingPhoto}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                    opacity: uploadingPhoto ? 0.7 : 1
                  }}
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      {t.uploading}
                    </>
                  ) : (
                    <>
                      <Camera size={20} />
                      {t.completeWithPhoto}
                    </>
                  )}
                </button>
              )}

              {/* Complete Button */}
              <button
                onClick={() => handleCompletePunchItem(false)}
                disabled={completingItems.has(selectedPunchItem.punch_id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px',
                  backgroundColor: completionPhoto ? '#22c55e' : '#1e3a5f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: completingItems.has(selectedPunchItem.punch_id) ? 'not-allowed' : 'pointer',
                  opacity: completingItems.has(selectedPunchItem.punch_id) ? 0.7 : 1
                }}
              >
                {completingItems.has(selectedPunchItem.punch_id) ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <CheckCircle size={20} />
                )}
                {completionPhoto ? t.completeItem : t.completeWithoutPhoto}
              </button>

              {/* Cancel Button */}
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setSelectedPunchItem(null);
                  setCompletionPhoto(null);
                }}
                style={{
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledge Button */}
      <button
        style={{
          ...styles.acknowledgeButton,
          ...(acknowledging ? styles.acknowledgeButtonDisabled : {})
        }}
        onClick={handleAcknowledge}
        disabled={acknowledging}
      >
        {acknowledging ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            {t.acknowledging}
          </>
        ) : (
          <>
            <CheckCircle size={20} />
            {t.acknowledge}
          </>
        )}
      </button>
    </div>
  );
};

export default SpecialRequirements;
